import axios from "axios";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, DatePicker, Form, Input, Modal, message } from "antd";
import { CameraOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import {
  differenceInDays,
  differenceInYears,
  endOfMonth,
  format,
  startOfMonth,
} from "date-fns";
import { useAuth } from "../../utils/useAuth";
import UserPayment from "./Components/UserPayment/UserPayment";
import UserLeaves from "./Components/UserLeaves/UserLeaves";
import { BASE_API_URL } from "../../utils/consts";
import { useTranslation } from "react-i18next";
import mAvatar from "../../assets/m-avatar.png";
import wAvatar from "../../assets/w-avatar.png";
import "./UserProfile.scss";
import UserProfileModal from "../../Components/Modals/UserProfileModal/UserProfileModal";
import { useDispatch, useSelector } from "react-redux";
import { setLoader } from "../../utils/redux/slices/loaderSlice";
import { setUserData } from "../../utils/redux/slices/userDataSlice";
import { uploadFileToR2 } from "../../utils/uploadToR2";
import { setWorkingPatterns } from "../../utils/redux/slices/workingPatternSlice";
import UserWorkLocations from "./Components/UserWorkLocations/UserWorkLocations";
import UserSuppliers from "./Components/UserSuppliers/UserSuppliers";
import {
  setClientLeaves,
  setClientOvertimes,
} from "../../utils/redux/slices/clientLeaves";
import {
  useGetWorkingPatternsQuery,
  useGetClientLeavesQuery,
  useGetClientOvertimesQuery,
} from "../../utils/redux/api/orgDataApi";
import { useGetUserPaymentsQuery } from "../../utils/redux/api/financeApi";
import UserOvertimes from "./Components/UserOvertimes/UserOvertimes";
import { isAdmin, isModerator, isSuperAdmin } from "../../utils/permissions";
import AddShiftModal from "../../Components/Modals/AddShiftModal/AddShiftModal";
import OvertimePayment from "../../Components/Modals/UserPayments/OvertimePayment/OvertimePayment";
import { ShiftTypePanorama } from "../Shifts/utils";

type Props = {};

const UserProfile: React.FC<Props> = () => {
  const params = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectedMonth, setSelectedMonth] = useState<any>(new Date());
  const [userDetails, setUserDetails] = useState<any>(undefined);
  const userData = useSelector((state: any) => state.userData.user);
  const [locations, setLocations] = useState<any>(undefined);
  const [activeTab, setActiveTab] = useState<string>("profile");
  const [isUserProfileModalOpen, setUserProfileModalOpen] =
    useState<boolean>(false);
  const [isFreezeModalOpen, setFreezeModalOpen] = useState<boolean>(false);
  const [isFreezeSubmitting, setFreezeSubmitting] = useState<boolean>(false);
  const [isApproving, setIsApproving] = useState<boolean>(false);
  const [isSetPasswordModalOpen, setSetPasswordModalOpen] = useState<boolean>(false);
  const [newPasswordValue, setNewPasswordValue] = useState("");
  const [confirmPasswordValue, setConfirmPasswordValue] = useState("");
  const [isSettingPassword, setIsSettingPassword] = useState<boolean>(false);

  const handleSetPassword = async () => {
    if (newPasswordValue.length < 6) {
      message.error(t("Password must be at least 6 characters"));
      return;
    }
    if (newPasswordValue !== confirmPasswordValue) {
      message.error(t("Passwords do not match"));
      return;
    }
    setIsSettingPassword(true);
    try {
      await axios.post(`${BASE_API_URL}/admin/set-user-password`, {
        userId: params.id,
        newPassword: newPasswordValue,
      });
      message.success(t("Password updated successfully"));
      setSetPasswordModalOpen(false);
      setNewPasswordValue("");
      setConfirmPasswordValue("");
    } catch (error: any) {
      message.error(error?.response?.data?.message || t("Error updating password"));
    } finally {
      setIsSettingPassword(false);
    }
  };
  const [isAddLeaveModalOpen, setIsAddLeaveModalOpen] = useState(false);
  const [isOvertimeQuickModalOpen, setIsOvertimeQuickModalOpen] = useState(false);
  const [documents, setDocuments] = useState<any[]>([]);
  const [freezeForm] = Form.useForm();
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const docInputRef = useRef<HTMLInputElement>(null);
  const authUser: any = useAuth();
  const isOwnProfile = authUser && authUser.id === params.id;
  const clientId: string =
    typeof userData?.clientId === "object"
      ? userData.clientId._id
      : userData?.clientId ?? "shared";

  useEffect(() => {
    const containsAdminRole = authUser.roles.some(
      (item: any) =>
        item.includes("ADMIN") ||
        item.includes("admin") ||
        item.includes("moderator") ||
        item.includes("MODERATOR") ||
        item.includes("ROLE_MODERATOR")
    );
    if (isOwnProfile || containsAdminRole) {
    } else {
      navigate("/");
    }
  }, [authUser, isOwnProfile, navigate]);

  const fetchUserDetails = useCallback(
    (showLoader = true) => {
      if (showLoader) dispatch(setLoader(true));
      return axios
        .get(
          `${BASE_API_URL}/users/${params.id}/${startOfMonth(
            new Date(selectedMonth)
          ).toISOString()}/${endOfMonth(new Date(selectedMonth)).toISOString()}`
        )
        .then((res) => {
          setUserDetails(res.data);
        })
        .catch((err) => console.error(err))
        .finally(() => showLoader && dispatch(setLoader(false)));
    },
    [dispatch, params.id, selectedMonth]
  );

  useEffect(() => {
    fetchUserDetails();
  }, [fetchUserDetails]);

  // Working patterns, leaves and overtimes are client-scoped reference data
  // shared with Staff/Shifts/settings pages — cached & deduped via RTK
  // Query instead of every page mount re-fetching it with raw axios. Other
  // code still reads these from their Redux slices, so keep dispatching
  // into them once the cached data arrives.
  const { data: workingPatternsData } = useGetWorkingPatternsQuery(userData?.clientId, {
    skip: !userData?.clientId,
  });
  const { data: clientLeavesData } = useGetClientLeavesQuery(userData?.clientId, {
    skip: !userData?.clientId,
  });
  const { data: clientOvertimesData } = useGetClientOvertimesQuery(userData?.clientId, {
    skip: !userData?.clientId,
  });

  useEffect(() => {
    if (workingPatternsData) dispatch(setWorkingPatterns(workingPatternsData));
  }, [workingPatternsData, dispatch]);
  useEffect(() => {
    if (clientLeavesData) dispatch(setClientLeaves(clientLeavesData));
  }, [clientLeavesData, dispatch]);
  useEffect(() => {
    if (clientOvertimesData) dispatch(setClientOvertimes(clientOvertimesData));
  }, [clientOvertimesData, dispatch]);

  useEffect(() => {
    if (params.id !== "new" && userData?.clientId) {
      axios
        .get(`${BASE_API_URL}/${userData?.clientId}/locations`)
        .then((res) => {
          setLocations(res.data);
        })
        .catch((err) => console.error(err));
    }
  }, [userData]);

  // Sync documents from user details
  useEffect(() => {
    if (userDetails?.user?.documents) {
      setDocuments(userDetails.user.documents);
    }
  }, [userDetails]);

  // Current-month salary data for Recent Activity — cached/deduped via RTK
  // Query so revisiting this profile (or another using the same shared
  // cache) within the cache window doesn't re-fetch it from scratch.
  const activityClientId = userDetails?.user?.clientId?._id;
  const activityMonthStart = format(startOfMonth(new Date()), "yyyy-MM-dd");
  const activityMonthEnd = format(endOfMonth(new Date()), "yyyy-MM-dd");
  const {
    data: activityData,
    refetch: refetchActivityData,
  } = useGetUserPaymentsQuery(
    {
      clientId: activityClientId,
      userId: params.id as string,
      startDate: activityMonthStart,
      endDate: activityMonthEnd,
    },
    { skip: !activityClientId || !params.id },
  );

  // Add shift/leave handler (mirrors Shifts.tsx logic)
  const handleAddShiftFromProfile = (value: any) => {
    const userId = userDetails?.user?._id;
    const clientId = userData?.clientId;
    if (!userId || !clientId) return;

    if (value.shiftType === ShiftTypePanorama.LEAVES) {
      const payload: any = {
        userId,
        clientId,
        startDate: value.date,
        shiftStartTime: "00:00",
        shiftEndTime: "23:59",
        shiftType: value.shiftType,
        timeFrame: value.timeFrame,
        excludeDays: [],
        customExcludeDates: value.excludedDates,
        leavePayPercentage: value.leavePayPercentage,
        leavePayType: value.leavePayType,
      };
      if (value.leavePayType === ShiftTypePanorama.HOURS) {
        payload.durationInHours = Number(value.durationInHours) || 0;
      }
      if (value.locationId) payload.locationId = value.locationId;

      axios
        .post(`${BASE_API_URL}/create-multiple-shifts`, payload)
        .then(() => {
          setIsAddLeaveModalOpen(false);
          message.success(t("Leave scheduled successfully"));
          fetchUserDetails(false);
        })
        .catch((err) => {
          console.error(err);
          message.error(t("Error scheduling leave"));
        });
      return;
    }

    if (value.isMultipleShift) {
      const payload: any = {
        userId,
        clientId,
        startDate: value.date,
        shiftStartTime: value.startTime?.format?.("HH:mm") ?? "00:00",
        shiftEndTime: value.endTime?.format?.("HH:mm") ?? "23:59",
        shiftType: value.shiftType,
        timeFrame: value.timeFrame,
        excludeDays: value.excludedWeekDays,
        customExcludeDates: value.excludedDates,
      };
      if (value.locationId) payload.locationId = value.locationId;
      axios
        .post(`${BASE_API_URL}/create-multiple-shifts`, payload)
        .then(() => {
          setIsAddLeaveModalOpen(false);
          message.success(t("Shifts added successfully"));
          fetchUserDetails(false);
        })
        .catch((err) => console.error(err));
      return;
    }

    const payload: any = {
      userId,
      clientId,
      date: value.date,
      startTime: value.startTime,
      endTime: value.endTime,
      shiftType: value.shiftType,
    };
    if (value.locationId) payload.locationId = value.locationId;
    axios
      .post(`${BASE_API_URL}/shifts/create`, payload)
      .then(() => {
        setIsAddLeaveModalOpen(false);
        message.success(t("Shift added successfully"));
        fetchUserDetails(false);
      })
      .catch((err) => console.error(err));
  };

  const onUpdateUserProfile = (profileData: any) => {
    dispatch(setLoader(true));

    axios
      .put(`${BASE_API_URL}/users/${params.id}`, {
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        phoneNumber: profileData.phoneNumber,
        color: profileData.color,
        position: profileData.position,
        gender: profileData.gender,
        amount: profileData.amount,
        workingPatternId: profileData.workingPatternId,
        positionInSector: profileData.positionInSector,
        leaves: profileData.clientLeaves,
        contractStart: profileData.contractStart,
        contractEnd: profileData.contractEnd,
        sanitaryBookletIssuedAt: profileData.sanitaryBookletIssuedAt,
        sanitaryBookletValidUntil: profileData.sanitaryBookletValidUntil,
        locationId: profileData.location,
        assignedLocationId: profileData.assignedLocationId || null,
        overtimeTypes: profileData.overtimeTypes,
        birthday: profileData.birthday,
      })
      .then(async () => {
        await fetchUserDetails(false);
        setUserProfileModalOpen(false);
        if (authUser && authUser.id === params.id) {
          const updatedUserData = { ...userData };
          updatedUserData.assignedLocationId =
            profileData.assignedLocationId || null;
          dispatch(setUserData(updatedUserData));
          localStorage.setItem("user", JSON.stringify(updatedUserData));
        }
      })
      .catch((err) => console.error(err))
      .finally(() => dispatch(setLoader(false)));
  };

  const handleAvatarFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    dispatch(setLoader(true));
    try {
      const uploaded = await uploadFileToR2(file, "avatars", clientId);
      await axios.put(`${BASE_API_URL}/users/${params.id}`, { avatar: uploaded });
      await fetchUserDetails(false);
      setUserProfileModalOpen(false);
    } catch (err) {
      console.error(err);
    } finally {
      dispatch(setLoader(false));
    }
  };

  const openFileStackDialog = () => avatarInputRef.current?.click();

  const handleDocumentFilesChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    e.target.value = "";
    try {
      const uploads = await Promise.all(files.map((f) => uploadFileToR2(f, "documents", clientId)));
      const responses = await Promise.all(
        uploads.map((u) =>
          axios.post(`${BASE_API_URL}/users/${params.id}/documents`, {
            handle: u.key,
            url: u.url,
            filename: u.filename,
            mimetype: u.mimetype,
            size: u.size,
          })
        )
      );
      const last = responses[responses.length - 1];
      setDocuments(last.data.documents ?? []);
    } catch (err) {
      console.error(err);
    }
  };

  const openDocumentPicker = () => docInputRef.current?.click();

  const handleDeleteDocument = (docId: string) => {
    axios
      .delete(`${BASE_API_URL}/users/${params.id}/documents/${docId}`)
      .then((res) => setDocuments(res.data.documents ?? []))
      .catch((err) => console.error(err));
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const onFreezeSubmit = () => {
    freezeForm
      .validateFields()
      .then((values) => {
        setFreezeSubmitting(true);
        const frozenUntilValue = values.frozenUntil
          ? values.frozenUntil.toDate().toISOString()
          : undefined;
        axios
          .post(`${BASE_API_URL}/users/${params.id}/freeze`, {
            reason: values.reason,
            frozenUntil: frozenUntilValue,
          })
          .then(() => {
            message.success(t("User frozen successfully"));
            return fetchUserDetails(false);
          })
          .then(() => setFreezeModalOpen(false))
          .catch((err) => {
            console.error(err);
            message.error(t("Error freezing user"));
          })
          .finally(() => setFreezeSubmitting(false));
      })
      .catch(() => {});
  };

  const onUnfreezeUser = () => {
    setFreezeSubmitting(true);
    axios
      .post(`${BASE_API_URL}/users/${params.id}/unfreeze`)
      .then(() => {
        message.success(t("User reactivated successfully"));
        return fetchUserDetails(false);
      })
      .catch((err) => {
        console.error(err);
        message.error(t("Error unfreezing user"));
      })
      .finally(() => setFreezeSubmitting(false));
  };

  const onApproveUser = () => {
    setIsApproving(true);
    axios
      .post(`${BASE_API_URL}/users/${params.id}/approve`)
      .then(() => {
        message.success(t("Account approved successfully"));
        return fetchUserDetails(false);
      })
      .catch((err) => {
        console.error(err);
        message.error(t("Error approving account"));
      })
      .finally(() => setIsApproving(false));
  };

  const openFreezeModal = () => {
    freezeForm.setFieldsValue({
      reason: userDetails?.user?.frozenReason,
      frozenUntil: userDetails?.user?.frozenUntil
        ? dayjs(userDetails.user.frozenUntil)
        : null,
    });
    setFreezeModalOpen(true);
  };

  const canManageFreeze =
    (isSuperAdmin(authUser?.roles) ||
      isAdmin(authUser?.roles) ||
      isModerator(authUser?.roles)) &&
    !isOwnProfile;

  // ── Derived values ──────────────────────────────────────────────────────────
  const u = userDetails?.user;
  const today = new Date();
  const initials =
    `${u?.firstName?.[0] ?? ""}${u?.lastName?.[0] ?? ""}`.toUpperCase() || "?";
  const avatarGradient = u?.color
    ? `linear-gradient(135deg, ${u.color}, ${u.color}88)`
    : "linear-gradient(135deg, oklch(0.7 0.13 30), oklch(0.55 0.16 320))";

  // Contract KPI
  const cStart = u?.contractStart ? new Date(u.contractStart) : null;
  const cEnd = u?.contractEnd ? new Date(u.contractEnd) : null;
  const contractProgressPct =
    cStart && cEnd
      ? Math.min(
          100,
          Math.max(
            0,
            Math.round(
              (differenceInDays(today, cStart) /
                differenceInDays(cEnd, cStart)) *
                100
            )
          )
        )
      : 0;
  const contractDaysElapsed = cStart ? differenceInDays(today, cStart) : 0;
  const contractDaysTotal =
    cStart && cEnd ? differenceInDays(cEnd, cStart) : 0;

  // Sanitary KPI
  const sanValidUntil = u?.sanitaryBookletValidUntil
    ? new Date(u.sanitaryBookletValidUntil)
    : null;
  const sanIssuedAt = u?.sanitaryBookletIssuedAt
    ? new Date(u.sanitaryBookletIssuedAt)
    : null;
  const sanDaysRemaining = sanValidUntil
    ? differenceInDays(sanValidUntil, today)
    : undefined;

  // Tenure KPI
  const tenureDays = cStart ? differenceInDays(today, cStart) : 0;
  const tenureYears = Math.floor(tenureDays / 365);
  const tenureMonths = Math.floor((tenureDays % 365) / 30);

  // Birthday KPI
  const bdDate = u?.birthday ? new Date(u.birthday) : null;
  const ageYears = bdDate ? differenceInYears(today, bdDate) : undefined;
  let daysUntilBirthday: number | undefined;
  if (bdDate) {
    let next = new Date(
      today.getFullYear(),
      bdDate.getMonth(),
      bdDate.getDate()
    );
    if (next <= today)
      next = new Date(
        today.getFullYear() + 1,
        bdDate.getMonth(),
        bdDate.getDate()
      );
    daysUntilBirthday = differenceInDays(next, today);
  }

  const frozenUntilFormatted = u?.frozenUntil
    ? format(new Date(u.frozenUntil), "dd MMM yyyy HH:mm")
    : undefined;

  const tabs = [
    { key: "profile", label: t("Profile") },
    { key: "payments", label: t("Payments") },
    // { key: "leaves", label: t("Leaves") },
    // { key: "overtimes", label: t("Overtimes") },
  ];

  return (
    <>
      <input
        ref={avatarInputRef}
        type="file"
        accept="image/jpeg,image/png,image/*"
        style={{ display: "none" }}
        onChange={handleAvatarFileChange}
      />
      <input
        ref={docInputRef}
        type="file"
        accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
        multiple
        style={{ display: "none" }}
        onChange={handleDocumentFilesChange}
      />
      <div className="up-page">
        {/* ── Hero ─────────────────────────────────────────────────────── */}
        <section className="up-hero">
          <div className="up-cover">
            <svg className="up-cover-dots" aria-hidden="true">
              <defs>
                <pattern
                  id="up-dots-pat"
                  width="22"
                  height="22"
                  patternUnits="userSpaceOnUse"
                >
                  <circle cx="2" cy="2" r="1.2" fill="#fff" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#up-dots-pat)" />
            </svg>
          </div>

          <div className="up-hero-body">
            {/* Avatar */}
            <div className="up-avatar-wrap">
              <div
                className="up-avatar"
                style={{
                  background: u?.avatar?.url ? undefined : avatarGradient,
                }}
              >
                {u?.avatar?.url ? (
                  <img src={u.avatar.url} alt="Profile" />
                ) : u?.gender === "male" ? (
                  <img src={mAvatar} alt="" />
                ) : u?.gender === "female" ? (
                  <img src={wAvatar} alt="" />
                ) : (
                  <span>{initials}</span>
                )}
                <button
                  className="up-avatar-camera"
                  type="button"
                  onClick={openFileStackDialog}
                  aria-label={t("Upload Photo")}
                >
                  <CameraOutlined />
                </button>
              </div>
              {!u?.isFrozen && <div className="up-status-dot" />}
            </div>

            {/* Info */}
            <div className="up-hero-info">
              <div className="up-hero-meta">
                <span className={`up-pill ${u?.isFrozen ? "rose" : "ok"} dot`}>
                  {u?.isFrozen ? t("Account Frozen") : t("Active employee")}
                </span>
                {!u?.isActive && (
                  <span className="up-pill warn">{t("Not activated")}</span>
                )}
                {u?.positionInSector?.slice(0, 2).map((pos: any) => (
                  <span className="up-pill" key={pos._id}>
                    {pos.name}
                    {pos.workSector?.name ? ` · ${pos.workSector.name}` : ""}
                  </span>
                ))}
                {u?._id && (
                  <span
                    className="up-pill up-mono"
                    style={{ fontSize: "10.5px" }}
                  >
                    ID {u._id.slice(-8)}
                  </span>
                )}
              </div>
              <h1 className="up-hero-name">
                {u?.firstName} {u?.lastName}
              </h1>
              <div className="up-hero-sub">
                {u?.username && (
                  <span className="up-hero-sub-item">
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                    >
                      <circle cx="12" cy="8" r="4" />
                      <path d="M4 21a8 8 0 0 1 16 0" />
                    </svg>
                    @{u.username}
                  </span>
                )}
                {u?.email && (
                  <>
                    {u?.username && <span className="up-sep" />}
                    <span className="up-hero-sub-item">
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                      >
                        <rect x="3" y="5" width="18" height="14" rx="2" />
                        <path d="M3 7l9 7 9-7" />
                      </svg>
                      {u.email}
                    </span>
                  </>
                )}
                {u?.location?.name && (
                  <>
                    <span className="up-sep" />
                    <span className="up-hero-sub-item">
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                      >
                        <path d="M12 22s-7-7-7-12a7 7 0 0 1 14 0c0 5-7 12-7 12z" />
                        <circle cx="12" cy="10" r="2.5" />
                      </svg>
                      {u.location.name}
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* CTA */}
            <div className="up-hero-cta">
              <button
                className="up-btn up-btn--ghost"
                onClick={() => setUserProfileModalOpen(true)}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 20h4L20 8l-4-4L4 16z" />
                </svg>
                {t("Edit profile")}
              </button>
              {isSuperAdmin(authUser?.roles) && !u?.isActive && (
                <Button
                  type="primary"
                  size="small"
                  onClick={onApproveUser}
                  loading={isApproving}
                  style={{ borderRadius: 8 }}
                >
                  {t("Approve account")}
                </Button>
              )}
              {isSuperAdmin(authUser?.roles) && (
                <Button
                  size="small"
                  onClick={() => setSetPasswordModalOpen(true)}
                  style={{ borderRadius: 8 }}
                >
                  {t("Set password")}
                </Button>
              )}
              {canManageFreeze && !u?.isFrozen && (
                <button
                  className="up-btn up-btn--danger"
                  onClick={openFreezeModal}
                  disabled={isFreezeSubmitting}
                >
                  {t("Freeze account")}
                </button>
              )}
              {canManageFreeze && u?.isFrozen && (
                <>
                  <button
                    className="up-btn up-btn--ghost"
                    onClick={openFreezeModal}
                    disabled={isFreezeSubmitting}
                  >
                    {t("Edit freeze")}
                  </button>
                  <button
                    className="up-btn up-btn--primary"
                    onClick={onUnfreezeUser}
                    disabled={isFreezeSubmitting}
                  >
                    {t("Unfreeze")}
                  </button>
                </>
              )}
              {u?.isFrozen && frozenUntilFormatted && (
                <span className="up-frozen-meta">
                  {t("until")}: {frozenUntilFormatted}
                </span>
              )}
            </div>
          </div>

          {/* KPI strip */}
          <div className="up-kpi-strip">
            {/* 1. Contract duration */}
            <div className="up-kpi">
              <div className="up-kpi-label">
                <span>{t("Contract duration")}</span>
                <span className="up-kpi-icon">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                  >
                    <rect x="3" y="5" width="18" height="16" rx="2" />
                    <path d="M3 9h18" />
                  </svg>
                </span>
              </div>
              {cStart && cEnd ? (
                <>
                  <div className="up-kpi-value">
                    <span className="up-mono">{contractProgressPct}%</span>
                    <span className="up-kpi-small">
                      {" "}
                      · {contractDaysElapsed} / {contractDaysTotal}{" "}
                      {t("days")}
                    </span>
                  </div>
                  <div className="up-progress">
                    <div style={{ width: `${contractProgressPct}%` }} />
                  </div>
                  <div className="up-progress-meta">
                    <span>{format(cStart, "dd MMM yyyy")}</span>
                    <span>{format(cEnd, "dd MMM yyyy")}</span>
                  </div>
                </>
              ) : (
                <div className="up-kpi-value up-muted">—</div>
              )}
            </div>

            {/* 2. Sanitary booklet */}
            <div className="up-kpi">
              <div className="up-kpi-label">
                <span>{t("Sanitary booklet")}</span>
                <span
                  className={`up-kpi-icon ${
                    sanDaysRemaining !== undefined && sanDaysRemaining < 30
                      ? "warn"
                      : "ok"
                  }`}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 3l8 3v6c0 5-4 8-8 9-4-1-8-4-8-9V6z" />
                    <path d="M9 12l2 2 4-4" />
                  </svg>
                </span>
              </div>
              <div className="up-kpi-value up-mono">
                {sanValidUntil ? format(sanValidUntil, "dd MMM yyyy") : "—"}
              </div>
              {sanIssuedAt && (
                <div className="up-kpi-sub">
                  {t("Issued")} {format(sanIssuedAt, "dd MMM")}
                  {sanDaysRemaining !== undefined &&
                    ` · ${
                      sanDaysRemaining > 0
                        ? `${t("valid")} ${sanDaysRemaining} ${t("days")}`
                        : t("expired")
                    }`}
                </div>
              )}
            </div>

            {/* 3. Working tenure */}
            <div className="up-kpi">
              <div className="up-kpi-label">
                <span>{t("Working tenure")}</span>
                <span className="up-kpi-icon">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                  >
                    <circle cx="9" cy="8" r="3.5" />
                    <path d="M2 20a7 7 0 0 1 14 0" />
                    <path d="M16 4a3.5 3.5 0 0 1 0 7M22 20a6 6 0 0 0-5-6" />
                  </svg>
                </span>
              </div>
              {cStart ? (
                <>
                  <div className="up-kpi-value">
                    {tenureYears > 0 && `${tenureYears}g `}
                    {tenureMonths}m{" "}
                    <span className="up-kpi-small">{t("in company")}</span>
                  </div>
                  <div className="up-kpi-sub">
                    {t("Start")}: {format(cStart, "dd MMM yyyy")}
                  </div>
                </>
              ) : (
                <div className="up-kpi-value up-muted">—</div>
              )}
            </div>

            {/* 4. Birthday */}
            <div className="up-kpi">
              <div className="up-kpi-label">
                <span>{t("Date of birth")}</span>
                <span className="up-kpi-icon warn">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M5 21V11h14v10z" />
                    <path d="M3 21h18M9 11V7M15 11V7" />
                  </svg>
                </span>
              </div>
              {bdDate ? (
                <>
                  <div className="up-kpi-value">
                    {format(bdDate, "dd MMM yyyy")}
                  </div>
                  <div className="up-kpi-sub">
                    {ageYears !== undefined && `${ageYears} ${t("years")}`}
                    {daysUntilBirthday !== undefined &&
                      ` · ${t("in")} ${daysUntilBirthday} ${t("days")}`}
                  </div>
                </>
              ) : (
                <div className="up-kpi-value up-muted">—</div>
              )}
            </div>
          </div>
        </section>

        {/* ── Tabs ─────────────────────────────────────────────────────── */}
        <div className="up-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`up-tab${activeTab === tab.key ? " up-tab--active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Content ──────────────────────────────────────────────────── */}
        {activeTab === "profile" ? (
          <div className="up-grid">
            {/* Left column */}
            <div className="up-col-stack">
              {/* Basic info card */}
              <div className="up-card">
                <div className="up-card-head">
                  <span className="up-card-ic">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.7"
                      strokeLinecap="round"
                    >
                      <circle cx="12" cy="8" r="4" />
                      <path d="M4 21a8 8 0 0 1 16 0" />
                    </svg>
                  </span>
                  <div className="up-card-titles">
                    <h3 className="up-card-title">{t("Basic information")}</h3>
                    <div className="up-card-sub">
                      {t("Employee personal data")}
                    </div>
                  </div>
                  <div className="up-card-actions">
                    <button
                      className="up-btn up-btn--ghost up-btn--sm"
                      onClick={() => setUserProfileModalOpen(true)}
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                      >
                        <path d="M4 20h4L20 8l-4-4L4 16z" />
                      </svg>
                      {t("Edit")}
                    </button>
                  </div>
                </div>
                <div className="up-card-body">
                  <div className="up-def">
                    <div className="up-def-row">
                      <div className="up-def-label">{t("First Name")}</div>
                      <div className="up-def-value">{u?.firstName || "—"}</div>
                    </div>
                    <div className="up-def-row">
                      <div className="up-def-label">{t("Last Name")}</div>
                      <div className="up-def-value">{u?.lastName || "—"}</div>
                    </div>
                    <div className="up-def-row">
                      <div className="up-def-label">{t("Username")}</div>
                      <div className="up-def-value up-mono">
                        {u?.username ? `@${u.username}` : "—"}
                      </div>
                    </div>
                    <div className="up-def-row">
                      <div className="up-def-label">{t("Email")}</div>
                      <div className="up-def-value">{u?.email || "—"}</div>
                    </div>
                    <div className="up-def-row">
                      <div className="up-def-label">{t("Phone Number")}</div>
                      <div className="up-def-value">{u?.phoneNumber || "—"}</div>
                    </div>
                    <div className="up-def-row">
                      <div className="up-def-label">{t("Gender")}</div>
                      <div className="up-def-value">
                        {u?.gender ? t(u.gender) : "—"}
                      </div>
                    </div>
                    <div className="up-def-row">
                      <div className="up-def-label">{t("Date of birth")}</div>
                      <div className="up-def-value up-mono">
                        {bdDate ? format(bdDate, "dd. MM. yyyy.") : "—"}
                      </div>
                    </div>
                    <div className="up-def-row">
                      <div className="up-def-label">{t("Location")}</div>
                      <div className="up-def-value">
                        {u?.location?.name || "—"}
                      </div>
                    </div>
                    <div className="up-def-row">
                      <div className="up-def-label">{t("Status")}</div>
                      <div className="up-def-value">
                        <span
                          className={`up-pill up-pill--sm ${u?.isFrozen ? "rose" : "ok"} dot`}
                        >
                          {u?.isFrozen ? t("Frozen") : t("Active")}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Work locations */}
              <UserWorkLocations
                userDetails={userDetails}
                onUpdate={() => fetchUserDetails(false)}
              />

              {/* Suppliers */}
              <UserSuppliers userDetails={userDetails} />

              {/* Documents */}
              <div className="up-card">
                <div className="up-card-head">
                  <span className="up-card-ic">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="9" y1="13" x2="15" y2="13" />
                      <line x1="9" y1="17" x2="15" y2="17" />
                    </svg>
                  </span>
                  <div className="up-card-titles">
                    <h3 className="up-card-title">{t("Documents")}</h3>
                    <div className="up-card-sub">{t("Files and attachments")}</div>
                  </div>
                  {documents.length > 0 && (
                    <div className="up-card-actions">
                      <button className="up-btn up-btn--ghost up-btn--sm" onClick={openDocumentPicker}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                        {t("Upload")}
                      </button>
                    </div>
                  )}
                </div>
                <div className="up-docs-body">
                  {documents.length === 0 ? (
                    <button className="up-docs-empty" onClick={openDocumentPicker}>
                      <span className="up-docs-empty-ic">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                      </span>
                      <span className="up-docs-empty-text">{t("Upload documents")}</span>
                      <span className="up-docs-empty-sub">PDF, images, Word, Excel · max 20 MB</span>
                    </button>
                  ) : (
                    <div className="up-docs-grid">
                      {documents.map((doc: any) => {
                        const isImage = doc.mimetype?.startsWith("image/");
                        const isPdf = doc.mimetype === "application/pdf";
                        const ext = doc.filename?.split(".").pop()?.toUpperCase() ?? "FILE";
                        return (
                          <div key={doc._id} className="up-doc-tile">
                            <div className="up-doc-preview">
                              {isImage ? (
                                <img src={doc.url} alt={doc.filename} className="up-doc-img" />
                              ) : isPdf ? (
                                <div className="up-doc-file-ic up-doc-file-ic--pdf">
                                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                    <polyline points="14 2 14 8 20 8" />
                                    <path d="M9 13h2a1 1 0 0 1 0 2H9v-4h2a1 1 0 0 1 0 2" />
                                  </svg>
                                  <span className="up-doc-file-type">{ext}</span>
                                </div>
                              ) : (
                                <div className="up-doc-file-ic">
                                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                    <polyline points="14 2 14 8 20 8" />
                                  </svg>
                                  <span className="up-doc-file-type">{ext}</span>
                                </div>
                              )}
                            </div>
                            <div className="up-doc-meta">
                              <div className="up-doc-name" title={doc.filename}>{doc.filename}</div>
                              {doc.size && <div className="up-doc-size">{formatFileSize(doc.size)}</div>}
                            </div>
                            <div className="up-doc-actions">
                              <a
                                href={doc.url}
                                target="_blank"
                                rel="noreferrer"
                                className="up-doc-action-btn"
                                title={t("Open")}
                              >
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                  <polyline points="15 3 21 3 21 9" />
                                  <line x1="10" y1="14" x2="21" y2="3" />
                                </svg>
                              </a>
                              <button
                                className="up-doc-action-btn up-doc-del"
                                onClick={() => handleDeleteDocument(doc._id)}
                                title={t("Delete")}
                              >
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="3 6 5 6 21 6" />
                                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                  <path d="M10 11v6M14 11v6" />
                                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right rail */}
            <aside className="up-col-stack">
              {/* Quick actions */}
              <div className="up-card">
                <div className="up-card-head">
                  <span className="up-card-ic">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.7"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M12 3l2 5 5 2-5 2-2 5-2-5-5-2 5-2z" />
                    </svg>
                  </span>
                  <div className="up-card-titles">
                    <h3 className="up-card-title">{t("Quick actions")}</h3>
                    <div className="up-card-sub">{t("Most frequent tasks")}</div>
                  </div>
                </div>
                <div className="up-quick">
                  <button
                    className="up-quick-cell"
                    onClick={() => setIsAddLeaveModalOpen(true)}
                  >
                    <span className="up-quick-ic">
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                      >
                        <path d="M12 3v2M3 12a9 9 0 0 1 18 0" />
                        <path d="M12 12v7a2 2 0 0 0 4 0" />
                      </svg>
                    </span>
                    <span className="up-quick-title">{t("Schedule leave")}</span>
                    <span className="up-quick-sub">{t("Manage time off")}</span>
                  </button>
                  <button
                    className="up-quick-cell"
                    onClick={() => setIsOvertimeQuickModalOpen(true)}
                  >
                    <span className="up-quick-ic">
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                      >
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 7v5l3 2" />
                      </svg>
                    </span>
                    <span className="up-quick-title">
                      {t("Report overtime")}
                    </span>
                    <span className="up-quick-sub">{t("Extra hours")}</span>
                  </button>
                  <button
                    className="up-quick-cell"
                    onClick={() => setActiveTab("payments")}
                  >
                    <span className="up-quick-ic">
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                      >
                        <rect x="2" y="6" width="20" height="12" rx="2" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    </span>
                    <span className="up-quick-title">{t("Payments")}</span>
                    <span className="up-quick-sub">
                      {t("View salary history")}
                    </span>
                  </button>
                </div>
              </div>

              {/* Recent activity — current month salary events */}
              <div className="up-card">
                <div className="up-card-head">
                  <span className="up-card-ic">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 12h4l3-8 4 16 3-8h4" />
                    </svg>
                  </span>
                  <div className="up-card-titles">
                    <h3 className="up-card-title">{t("Recent activity")}</h3>
                    <div className="up-card-sub">{format(new Date(), "MMMM yyyy")}</div>
                  </div>
                </div>
                <div className="up-card-body up-activity-list">
                  {(() => {
                    const items: Array<{ key: string; icon: React.ReactNode; text: string; meta: string; variant: string }> = [];

                    // Bonus
                    (activityData?.filteredUserPayments?.bonus || []).forEach((b: any, i: number) => {
                      items.push({
                        key: `bonus-${i}`,
                        icon: (
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><circle cx="12" cy="9" r="5"/><path d="M8.5 13L7 21l5-3 5 3-1.5-8"/></svg>
                        ),
                        text: `Bonus: +${Number(b.total || 0).toLocaleString("sr-RS")} RSD`,
                        meta: b.description || (b.date ? format(new Date(b.date), "dd MMM yyyy") : ""),
                        variant: "ok",
                      });
                    });

                    // Overtime
                    (activityData?.filteredUserPayments?.overtime || []).forEach((o: any, i: number) => {
                      items.push({
                        key: `ot-${i}`,
                        icon: (
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
                        ),
                        text: `Prekovremeni: +${Number(o.total || 0).toLocaleString("sr-RS")} RSD`,
                        meta: o.description || (o.date ? format(new Date(o.date), "dd MMM yyyy") : ""),
                        variant: "ok",
                      });
                    });

                    // Penalty
                    (activityData?.filteredUserPayments?.penalty || []).forEach((p: any, i: number) => {
                      items.push({
                        key: `pen-${i}`,
                        icon: (
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M9 9l6 6M15 9l-6 6"/></svg>
                        ),
                        text: `Penal: −${Number(p.total || 0).toLocaleString("sr-RS")} RSD`,
                        meta: p.description || (p.date ? format(new Date(p.date), "dd MMM yyyy") : ""),
                        variant: "rose",
                      });
                    });

                    // Travel expenses — from salarySummary or salary
                    const travelAmt = activityData?.salarySummary?.travel;
                    if (travelAmt && travelAmt > 0) {
                      items.push({
                        key: "travel",
                        icon: (
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><circle cx="12" cy="6" r="3"/><path d="M6 21l4-9h4l4 9"/></svg>
                        ),
                        text: `Putni troškovi: +${Number(travelAmt).toLocaleString("sr-RS")} RSD`,
                        meta: format(new Date(), "MMMM yyyy"),
                        variant: "warn",
                      });
                    }

                    // Loans active this month
                    (activityData?.filteredUserPayments?.loan || []).forEach((l: any, i: number) => {
                      const ltype =
                        l.type === "advance_salary" ? "Plata unapred" :
                        l.type === "flexible_payout" ? "Fleksibilna pozajmica" :
                        "Pozajmica na rate";
                      items.push({
                        key: `loan-${i}`,
                        icon: (
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><path d="M3 12l4-4 3 3 5-5 6 6-4 4-3-3-5 5z"/></svg>
                        ),
                        text: `${ltype}: −${Number(l.totalAmount || 0).toLocaleString("sr-RS")} RSD`,
                        meta: l.startDate ? format(new Date(l.startDate), "dd MMM yyyy") : "",
                        variant: "rose",
                      });
                    });

                    // Shortage / overage
                    (activityData?.filteredUserPayments?.shortageOverage || []).forEach((s: any, i: number) => {
                      const isShortage = s.type === "shortage";
                      items.push({
                        key: `so-${i}`,
                        icon: (
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><path d="M3 6h18M3 12h18M3 18h12"/></svg>
                        ),
                        text: `${isShortage ? "Manjak" : "Višak"}: ${isShortage ? "−" : "+"}${Number(s.total || 0).toLocaleString("sr-RS")} RSD`,
                        meta: s.date ? format(new Date(s.date), "dd MMM yyyy") : "",
                        variant: isShortage ? "rose" : "ok",
                      });
                    });

                    // Payout to bank
                    const monthKey = format(new Date(), "yyyy-MM");
                    (activityData?.filteredUserPayments?.payoutToBank || activityData?.payoutToBank || [])
                      .filter((p: any) => p.month === monthKey)
                      .forEach((p: any, i: number) => {
                        items.push({
                          key: `payout-${i}`,
                          icon: (
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><path d="M12 4v12M6 12l6 6 6-6"/></svg>
                          ),
                          text: `Isplata na račun: ${Number(p.total || 0).toLocaleString("sr-RS")} RSD`,
                          meta: p.description || format(new Date(), "MMMM yyyy"),
                          variant: "accent",
                        });
                      });

                    if (items.length === 0) {
                      return <div className="up-empty-state">{t("No recent activity")}</div>;
                    }

                    return items.map((item) => (
                      <div key={item.key} className="up-activity-item">
                        <span className={`up-activity-ic up-activity-ic--${item.variant}`}>{item.icon}</span>
                        <div className="up-activity-body">
                          <div className="up-activity-text">{item.text}</div>
                          {item.meta && <div className="up-activity-meta">{item.meta}</div>}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </div>

              {/* Key dates card */}
              {(cStart || sanValidUntil || bdDate) && (
                <div className="up-card">
                  <div className="up-card-head">
                    <span className="up-card-ic">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                      >
                        <path d="M3 3v18h18" />
                        <path d="M7 14l4-4 3 3 5-6" />
                      </svg>
                    </span>
                    <div className="up-card-titles">
                      <h3 className="up-card-title">{t("Key dates")}</h3>
                      <div className="up-card-sub">
                        {t("Important milestones")}
                      </div>
                    </div>
                  </div>
                  <div className="up-card-body">
                    {cStart && (
                      <div className="up-util-row">
                        <span className="up-util-label">
                          {t("Contract start")}
                        </span>
                        <span className="up-mono up-util-value">
                          {format(cStart, "dd MMM yyyy")}
                        </span>
                      </div>
                    )}
                    {cEnd && (
                      <div className="up-util-row">
                        <span className="up-util-label">
                          {t("Contract end")}
                        </span>
                        <span className="up-mono up-util-value">
                          {format(cEnd, "dd MMM yyyy")}
                        </span>
                      </div>
                    )}
                    {sanValidUntil && (
                      <div className="up-util-row">
                        <span className="up-util-label">
                          {t("Sanitary booklet")}
                        </span>
                        <span
                          className={`up-mono up-util-value${
                            sanDaysRemaining !== undefined &&
                            sanDaysRemaining < 30
                              ? " warn"
                              : ""
                          }`}
                        >
                          {format(sanValidUntil, "dd MMM yyyy")}
                        </span>
                      </div>
                    )}
                    {cStart && (
                      <div className="up-util-row">
                        <span className="up-util-label">{t("Tenure")}</span>
                        <span className="up-mono up-util-value">
                          {tenureYears > 0 && `${tenureYears}g `}
                          {tenureMonths}m
                        </span>
                      </div>
                    )}
                    {bdDate && (
                      <div className="up-util-row">
                        <span className="up-util-label">
                          {t("Date of birth")}
                        </span>
                        <span className="up-mono up-util-value">
                          {format(bdDate, "dd MMM yyyy")}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </aside>
          </div>
        ) : (
          <div className="up-tab-content">
            {activeTab === "payments" && (
              <UserPayment
                userDetails={userDetails}
                onChangeDate={(date: any) => setSelectedMonth(date)}
                onUpdateUser={() => fetchUserDetails(false)}
              />
            )}
            {activeTab === "leaves" && (
              <UserLeaves userDetails={userDetails} />
            )}
            {activeTab === "overtimes" && (
              <UserOvertimes userDetails={userDetails} />
            )}
          </div>
        )}
      </div>

      {/* ── Modals ───────────────────────────────────────────────────────── */}

      {/* Schedule leave quick action modal */}
      {isAddLeaveModalOpen && userDetails?.user && (
        <AddShiftModal
          isOpen={isAddLeaveModalOpen}
          onCloseModal={() => setIsAddLeaveModalOpen(false)}
          onAddShift={handleAddShiftFromProfile}
          shiftModalOpen={{ user: userDetails.user }}
          defaultShiftType={ShiftTypePanorama.LEAVES}
        />
      )}

      {/* Overtime quick action modal */}
      {isOvertimeQuickModalOpen && (
        <OvertimePayment
          isOpen={isOvertimeQuickModalOpen}
          onCloseModal={() => setIsOvertimeQuickModalOpen(false)}
          mode="create"
          selectedMonth={dayjs(new Date())}
          userId={params.id}
          onUpdate={() => {
            refetchActivityData();
          }}
        />
      )}

      <Modal
        open={isSetPasswordModalOpen}
        title={t("Set password")}
        okText={t("Save")}
        cancelText={t("Cancel")}
        className="standardHeader standardFooter"
        confirmLoading={isSettingPassword}
        onOk={handleSetPassword}
        onCancel={() => {
          setSetPasswordModalOpen(false);
          setNewPasswordValue("");
          setConfirmPasswordValue("");
        }}
      >
        <Form layout="vertical">
          <Form.Item label={t("New password")}>
            <Input.Password
              value={newPasswordValue}
              onChange={(e) => setNewPasswordValue(e.target.value)}
              autoComplete="new-password"
            />
          </Form.Item>
          <Form.Item label={t("Confirm password")}>
            <Input.Password
              value={confirmPasswordValue}
              onChange={(e) => setConfirmPasswordValue(e.target.value)}
              autoComplete="new-password"
              onPressEnter={handleSetPassword}
            />
          </Form.Item>
        </Form>
      </Modal>
      {isUserProfileModalOpen && (
        <UserProfileModal
          isOpen={isUserProfileModalOpen}
          onCloseModal={() => setUserProfileModalOpen(false)}
          onUpdateProfile={onUpdateUserProfile}
          userDetails={userDetails}
          locations={locations}
        />
      )}
      <Modal
        open={isFreezeModalOpen}
        title={t("Freeze account")}
        okText={t("Save")}
        cancelText={t("Cancel")}
        className="standardHeader standardFooter"
        confirmLoading={isFreezeSubmitting}
        onOk={onFreezeSubmit}
        onCancel={() => setFreezeModalOpen(false)}
      >
        <Form
          layout="vertical"
          form={freezeForm}
          initialValues={{
            reason: userDetails?.user?.frozenReason,
            frozenUntil: userDetails?.user?.frozenUntil
              ? dayjs(userDetails.user.frozenUntil)
              : null,
          }}
        >
          <Form.Item
            label={t("Reason")}
            name="reason"
            rules={[
              {
                max: 200,
                message: t("Reason should be under 200 characters"),
              },
            ]}
          >
            <Input.TextArea
              placeholder={t("Optional reason")}
              rows={3}
              allowClear
            />
          </Form.Item>
          <Form.Item label={t("Frozen until")} name="frozenUntil">
            <DatePicker
              showTime
              style={{ width: "100%" }}
              allowClear
              placeholder={t("Leave empty for indefinite freeze")}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default UserProfile;
